import React, {
  useRef,
  useState,
  useEffect,
  useContext,
  Fragment,
} from 'react';
import {
  node,
  bool,
  shape,
  oneOf,
} from 'prop-types';

const FreezeContext = React.createContext();
const isServerSide = () => typeof window === 'undefined';

class FreezeComponentCount {
  value = 0;
}

const FreezeProvider = ({ children, ssrFreezeComponentCount }) => {
  const [isForceHydrate, setIsForceHydrate] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [intersectionObserver, setIntersectionObserver] = useState(null);
  const [freezeComponentCount, setFreezeComponentCount] = useState(0);

  // After all freeze components are mounted after ssr
  // Set force hydrate to true so that the component will be hydrated right away in next remount
  useEffect(() => {
    if (freezeComponentCount >= ssrFreezeComponentCount.value && !isForceHydrate) {
      setIsForceHydrate(true);
    }
  }, [
    isForceHydrate,
    ssrFreezeComponentCount,
    freezeComponentCount,
  ]);

  useEffect(() => {
    if ('IntersectionObserver' in window) {
      const callback = (entries) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (target.isHydrate() || !isIntersecting) return;
          target.setHydrate(true);
        });
      };

      const options = {
        rootMargin: '0px 0px 250px 0px',
        threshold: 0.1,
      };

      setIntersectionObserver(new IntersectionObserver(callback, options));
    }

    setIsInitialized(true);
  }, []);

  return (
    <FreezeContext.Provider
      value={{
        intersectionObserver,
        isInitialized,
        isForceHydrate,
        ssrFreezeComponentCount,
        setFreezeComponentCount,
      }}
    >
      {children}
    </FreezeContext.Provider>
  );
};

FreezeProvider.propTypes = {
  children: node.isRequired,
  ssrFreezeComponentCount: shape({}).isRequired,
};

const Freeze = ({ freeze, children, until }) => {
  const divRef = useRef(null);
  const ssrFreezeComponentCountRef = useRef(null);
  const {
    intersectionObserver,
    isInitialized,
    isForceHydrate,
    ssrFreezeComponentCount,
    setFreezeComponentCount,
  } = useContext(FreezeContext);
  const [isHydrate, setHydrate] = useState(isForceHydrate);

  if (isServerSide() && !ssrFreezeComponentCountRef.current) {
    ssrFreezeComponentCountRef.current = ssrFreezeComponentCount;
    ssrFreezeComponentCountRef.current.value++;
  }

  useEffect(() => {
    setFreezeComponentCount(prev => prev + 1);
  }, []);

  useEffect(() => {
    const { current: divEl } = divRef;
    if (divEl) {
      switch (until) {
        case 'visible':
          if (!intersectionObserver) break;
          divEl.isHydrate = () => isHydrate;
          divEl.setHydrate = setHydrate;
          intersectionObserver.observe(divEl);
          break;
        default:
          break;
      }
    }

    // Clean up
    return () => {
      if (divEl) {
        switch (until) {
          case 'visible':
            if (!intersectionObserver) break;
            intersectionObserver.unobserve(divEl);
            break;
          default:
            break;
        }
      }
    };
  }, [until, intersectionObserver]);

  const finalIsHydrate = until === 'visible'
    ? (isInitialized && (!intersectionObserver || isHydrate))
    : isHydrate;

  return !freeze || isServerSide() || finalIsHydrate
    ? (
      <Fragment>
        {children}
      </Fragment>
    )
    : (
      <div
        ref={divRef}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: '' }}
        suppressHydrationWarning={true}
      />
    );
};

Freeze.propTypes = {
  freeze: bool,
  children: node.isRequired,
  until: oneOf(['visible']),
};

Freeze.defaultProps = {
  freeze: true,
  until: 'visible',
};

export default Freeze;
export {
  FreezeProvider,
  FreezeComponentCount,
};
